import CircularProgress from '@material-ui/core/CircularProgress';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import React, { useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { useDispatch, useSelector } from 'react-redux';

import { Dispatch } from 'src/config/Store';
import { FetchStatus, Conversation } from 'src/models/Chat';
import {
  getConversationsWithMessages,
  getFetchStatus,
  getNewMatchConversations,
  getPageInfo,
} from 'src/selectors/chatSelectors';

import ChatMember from './ChatMember';
import ChatPanelHeader from './ChatPanelHeader';

enum ChatMemberListTestID {
  Container = 'ChatMemberListTestID.Container',
}

enum ChatNewMatchMemberListTestID {
  Container = 'ChatNewMatchMemberListTestID.Container',
}

export const PaginatedChatMemberList = ({ hideMembersList }) => {
  const dispatch = useDispatch<Dispatch>();

  const conversationsWithMessages = useSelector(getConversationsWithMessages);
  const newMatchConversations = useSelector(getNewMatchConversations);
  const conversationFetchStatus = useSelector(getFetchStatus);
  const { hasNextPage } = useSelector(getPageInfo);
  const [isLoading, setIsLoading] = useState(false);

  const getKey = (conversation) =>
    conversation.uuid + (get(conversation, 'newestMessage.uuid ') || 0);

  if (conversationsWithMessages.length < 1 && newMatchConversations.length < 1) {
    return null;
  }

  const handleMemberSelect = async (conversation: Conversation) => {
    await dispatch.chat.setActiveConversation(conversation.uuid);
    await dispatch.chat.setActiveConversationTopicsOfFocus(conversation.member.uuid);
    hideMembersList();
  };

  return (
    <div
      style={{ paddingBottom: 100, overflowY: 'scroll' }}
      data-testid={ChatMemberListTestID.Container}
    >
      {/* Note: The component will fetch multuple times if we don't add the check for loading
      That is why we manually add the loader below too */}
      <InfiniteScroll
        hasMore={conversationFetchStatus === FetchStatus.Completed && hasNextPage && !isLoading}
        loadMore={async (page) => {
          setIsLoading(true);
          await dispatch.chat.fetchNextPage(page);
          setIsLoading(false);
        }}
        useWindow={false}
        threshold={100}
      >
        {newMatchConversations.length > 0 ? (
          <div data-testid={ChatNewMatchMemberListTestID.Container}>
            <ChatPanelHeader
              title="Connect with your new matches"
              description="Matches that you haven’t yet chatted with will appear here"
            />
            {newMatchConversations.map((conversation) => (
              <ChatMember
                conversation={conversation}
                key={getKey(conversation)}
                onClickMember={() => handleMemberSelect(conversation)}
              />
            ))}
          </div>
        ) : null}
        {conversationsWithMessages.length > 0 ? (
          <>
            <ChatPanelHeader title="Clients" />
            {orderBy(
              conversationsWithMessages,
              (c) => get(c, 'newestMessage.sentAt', 0),
              'desc',
            ).map((conversation) => (
              <ChatMember
                conversation={conversation}
                key={getKey(conversation)}
                onClickMember={() => handleMemberSelect(conversation)}
              />
            ))}
          </>
        ) : null}
      </InfiniteScroll>
      {conversationFetchStatus === FetchStatus.LoadingMore && <CircularProgress size={30} />}
    </div>
  );
};
